import React, { useEffect, useMemo, useState } from "react";

import { Grid, IconButton, MenuItem, SelectChangeEvent } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Option } from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { FiMinusCircle } from "react-icons/fi";
import * as yup from "yup";

import getShipmentOptions from "../../../../../hooks/getShipmentOptions";
import useYupValidation from "../../../../../hooks/useYupValidation";
import { Shipment, ShipmentContainerInfo } from "../../../../../models/shipments";
import { handleGetConvertValue } from "../../../../../util/converts";
import {
  ShipmentContainerInfoDetails,
  ShipmentDetails,
  ShipmentLoadType,
  ShipmentObjects,
  ShipmentType,
} from "../../../../../util/enums";
import { DecimalFormatType } from "../../../../../util/formatDecimalValue";
import { getMeasurement, getWeightMeasurement } from "../../../../../util/getMeasurement";
import { Input, Select } from "../../../../Form";
import Checkbox from "../../../../Form/Checkbox";
import InputMeasurement from "../../../../Form/InputMeasurement";
import { Theme } from "@mui/material/styles";

interface Props {
  index: number;
  containerInfo: ShipmentContainerInfo;
  viewOnly?: boolean;
  isShowDeleteButton?: boolean;
  errors?: yup.ValidationError[];
  shipment?: Shipment;
  handleUpdateShipment: (attribute: keyof Shipment | "all", value: any) => void;
  handleChangeOptions: (object: any, noChanges?: boolean) => void;
}

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    minHeight: "56px",
    display: "flex",
    alignItems: "center",
  },
  formField: {
    [theme.breakpoints.up("md")]: {
      width: (props: { isShowDeleteButton: boolean }) =>
        props.isShowDeleteButton ? `12.3%` : `${100 / 8}%`,
      flexBasis: "auto",
    },
  },
  deleteButton: {},
}));

export const ContainerDetails: React.FC<Props> = (props) => {
  const {
    containerInfo: containerInfoProp,
    isShowDeleteButton = false,
    errors = [],
    index,
    viewOnly = false,
    shipment,
    handleUpdateShipment,
    handleChangeOptions,
  } = props;
  const { t: translate } = useTranslation();

  const { containerTypeOptions, containerTypeOptionsOnlyForSeaAndFCLLoadType } =
    getShipmentOptions();

  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const deciamlFormatTypeForWeight = measurementWeight?.decimalPlaces || DecimalFormatType.MAERSK;
  const deciamlFormatTypeForVolume = measurement?.decimalPlaces || DecimalFormatType.MAERSK;

  const [containerInfo, setContainerInfo] = useState<ShipmentContainerInfo>(containerInfoProp);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const updateShipmentContainer = (attribute: keyof ShipmentContainerInfo, value: any) => {
    const containers = _.cloneDeep(shipment?.containers || [{}]);

    if (containers?.length) {
      containers[index][attribute] = value;

      handleUpdateShipment("all", {
        [ShipmentDetails.CONTAINERS]: containers,
        [ShipmentDetails.CONTAINER_NUMBERS]: containers
          .map((item) => item?.container)
          .filter((value) => value !== "")
          .join("-"),
      });
      handleChangeOptions(ShipmentObjects.CONTAINERS);
    }
  };

  const onRemovingContainer = () => {
    const containers = _.cloneDeep(shipment?.containers);

    if (containers?.length) {
      containers.splice(index, 1);

      handleUpdateShipment("all", {
        [ShipmentDetails.CONTAINERS]: containers,
        [ShipmentDetails.CONTAINER_NUMBERS]: containers
          ?.map((item) => item?.container)
          .filter((value) => value !== "")
          .join("-"),
      });

      handleChangeOptions(ShipmentObjects.CONTAINERS);
    }
  };

  const containerTypeOptionsByLoadType = useMemo(() => {
    if (shipment?.shipmentType === ShipmentType.SEA) {
      if (shipment?.loadType === ShipmentLoadType.FCL)
        return containerTypeOptionsOnlyForSeaAndFCLLoadType;

      return containerTypeOptions;
    }

    return containerTypeOptions;
  }, [shipment?.loadType, shipment?.shipmentType]);

  const tareWeightValue = useMemo(() => {
    if (Number(containerInfo?.grossWeight) >= 0 && Number(containerInfo?.netWeight) >= 0) {
      const tareWeight = Number(containerInfo?.grossWeight) - Number(containerInfo?.netWeight);

      updateShipmentContainer(ShipmentContainerInfoDetails.TARE_WEIGHT, tareWeight);
      return tareWeight;
    }
  }, [containerInfo?.grossWeight, containerInfo?.netWeight]);

  const volumetricWeightValue = useMemo(() => {
    if (Number(containerInfo?.grossVolume) > 0) {
      const volumetricWeight = Number(containerInfo?.grossVolume) / 1000;
      updateShipmentContainer(ShipmentContainerInfoDetails.VOLUMETRIC_WEIGHT, volumetricWeight);
      return volumetricWeight;
    }
  }, [containerInfo?.grossVolume]);

  useEffect(() => {
    if (shipment?.loadType) {
      const containerSizesValues = containerTypeOptionsByLoadType.map((size) => size.value);

      if (containerInfo?.size && !containerSizesValues.includes(containerInfo?.size)) {
        setContainerInfo((prev) => ({
          ...prev,
          [ShipmentContainerInfoDetails.SIZE]: "",
        }));
        updateShipmentContainer(ShipmentContainerInfoDetails.SIZE, "");
      }
    }
  }, [shipment?.loadType]);

  useEffect(() => {
    if (containerInfo?.size?.length) {
      const containerSize = containerTypeOptionsByLoadType.find(
        (size) => size.value === containerInfo?.size
      );

      if (containerSize) {
        const volume =
          Number(containerSize?.lengthSize) *
          Number(containerSize?.width) *
          Number(containerSize?.height);
        const convertedVolume = handleGetConvertValue(
          volume,
          measurement?.code,
          deciamlFormatTypeForVolume
        );

        updateShipmentContainer(ShipmentContainerInfoDetails.GROSS_VOLUME, convertedVolume);
        setContainerInfo((prev) => ({ ...prev, grossVolume: Number(convertedVolume) }));
      }
    } else {
      setContainerInfo((prev) => ({ ...prev, grossVolume: 0.0 }));
      updateShipmentContainer(ShipmentContainerInfoDetails.GROSS_VOLUME, 0.0);
    }
  }, [containerInfo?.size]);

  useEffect(() => {
    if (containerInfo?.verifiedGrossWeight) {
      if (
        containerInfo?.grossWeight &&
        (containerInfo?.netWeight === null || containerInfo?.netWeight === undefined)
      ) {
        updateShipmentContainer(ShipmentContainerInfoDetails.NET_WEIGHT, 0);
        setContainerInfo((prev) => ({ ...prev, netWeight: 0 }));
      }
    }
  }, [containerInfo?.verifiedGrossWeight, containerInfo?.grossWeight]);

  const classes = useStyles({ isShowDeleteButton });

  return (
    <div
      style={{
        padding: "4px",
      }}
    >
      <Grid container spacing={1} className={classes.container}>
        {/* Container Number */}
        <Grid item xs={12} sm={6} className={classes.formField}>
          <Input
            placeholder={translate("containerCase")}
            required
            value={containerInfo?.[ShipmentContainerInfoDetails.CONTAINER]}
            error={fieldHasError(`containers[${index}].container`)}
            onChange={(event) =>
              setContainerInfo((prev) => ({
                ...prev,
                [ShipmentContainerInfoDetails.CONTAINER]: event.target.value,
              }))
            }
            onBlur={(event) =>
              updateShipmentContainer(ShipmentContainerInfoDetails.CONTAINER, event.target.value)
            }
            disabled={viewOnly}
          />
        </Grid>

        {/* Case Size */}
        <Grid item xs={12} sm={6} className={classes.formField}>
          <Select
            defaultValue={""}
            value={containerInfo?.[ShipmentContainerInfoDetails.SIZE]}
            placeholder={translate("selectAContainerCaseSize")}
            disabled={viewOnly}
            onChange={(event: SelectChangeEvent<any>) =>
              setContainerInfo((prev) => ({
                ...prev,
                [ShipmentContainerInfoDetails.SIZE]: event.target.value,
              }))
            }
            onBlur={(event) =>
              updateShipmentContainer(ShipmentContainerInfoDetails.SIZE, event.target.value)
            }
          >
            {containerTypeOptions.map((option: Option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        {/* Seal / Markings */}
        <Grid item xs={12} sm={6} className={classes.formField}>
          <Input
            placeholder={translate("sealMarkings")}
            value={containerInfo?.[ShipmentContainerInfoDetails.SEAL]}
            disabled={viewOnly}
            onChange={(event) =>
              setContainerInfo((prev) => ({
                ...prev,
                [ShipmentContainerInfoDetails.SEAL]: event.target.value,
              }))
            }
            onBlur={(event) =>
              updateShipmentContainer(ShipmentContainerInfoDetails.SEAL, event.target.value)
            }
          />
        </Grid>

        {/* Gross Weight */}
        <Grid item xs={12} sm={6} className={classes.formField}>
          <InputMeasurement
            measurement={measurementWeight}
            decimalPlacesCustom={deciamlFormatTypeForWeight}
            value={containerInfo?.[ShipmentContainerInfoDetails.GROSS_WEIGHT] || 0}
            change={(value) => {
              setContainerInfo((prev) => ({
                ...prev,
                [ShipmentContainerInfoDetails.GROSS_WEIGHT]: value as any,
              }));
              updateShipmentContainer(ShipmentContainerInfoDetails.GROSS_WEIGHT, value);
            }}
            showMeasurement
            disabled={viewOnly}
          />
        </Grid>

        {/* Net Weight */}
        <Grid item xs={12} sm={6} className={classes.formField}>
          <InputMeasurement
            measurement={measurementWeight}
            decimalPlacesCustom={deciamlFormatTypeForWeight}
            value={containerInfo?.[ShipmentContainerInfoDetails.NET_WEIGHT] || 0}
            change={(value) => {
              setContainerInfo((prev) => ({
                ...prev,
                [ShipmentContainerInfoDetails.NET_WEIGHT]: value,
              }));
              updateShipmentContainer(ShipmentContainerInfoDetails.NET_WEIGHT, value);
            }}
            showMeasurement
            disabled={viewOnly}
          />
        </Grid>

        {/* Tare Weight */}
        <Grid item xs={12} sm={6} className={classes.formField}>
          <InputMeasurement
            min="0"
            value={tareWeightValue || 0}
            measurement={measurementWeight}
            showMeasurement
            decimalPlacesCustom={deciamlFormatTypeForWeight}
            disabled
            infoMessage={`${translate("grossWeight")} - ${translate("netWeight")}`}
          />
        </Grid>

        {/* Gross Volume */}
        <Grid item xs={12} sm={6} className={classes.formField}>
          <InputMeasurement
            min="0"
            value={containerInfo?.[ShipmentContainerInfoDetails.GROSS_VOLUME] || 0}
            change={(value) => {
              setContainerInfo((prev) => ({
                ...prev,
                [ShipmentContainerInfoDetails.GROSS_VOLUME]: value,
              }));
              updateShipmentContainer(ShipmentContainerInfoDetails.GROSS_VOLUME, value);
            }}
            measurement={measurement}
            showMeasurement
            decimalPlacesCustom={deciamlFormatTypeForVolume}
            disabled={viewOnly}
            error={fieldHasError(`containers[${index}].grossVolume`)}
            errorMessage={getErrorByField(`containers[${index}].grossVolume`)}
          />
        </Grid>

        {/* Volumetric Weight */}
        <Grid item xs={12} sm={6} className={classes.formField}>
          <InputMeasurement
            value={volumetricWeightValue || 0}
            showMeasurement
            disabled
            measurement={measurementWeight}
          />
        </Grid>

        {/* Remove Button */}
        {!viewOnly && isShowDeleteButton && (
          <div className={classes.deleteButton}>
            <IconButton
              onClick={onRemovingContainer}
              style={{
                padding: "0px",
              }}
            >
              <FiMinusCircle color={colors.error500} size={18} />
            </IconButton>
          </div>
        )}
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6} className={classes.formField}></Grid>
        <Grid item xs={12} sm={6} className={classes.formField}></Grid>
        <Grid item xs={12} sm={6} className={classes.formField}></Grid>
        <Grid
          item
          xs={12}
          sm={6}
          style={{
            width: "62.5%",
          }}
        >
          <p className="" style={{ width: "fit-content" }}>
            <Checkbox
              label={translate("verifiedGrossWeight")}
              checked={containerInfo?.[ShipmentContainerInfoDetails.VERIFIED_GROSS_WEIGHT]}
              onChange={(_, checked) =>
                setContainerInfo((prev) => ({
                  ...prev,
                  [ShipmentContainerInfoDetails.VERIFIED_GROSS_WEIGHT]: checked,
                }))
              }
              onBlur={() =>
                updateShipmentContainer(
                  ShipmentContainerInfoDetails.VERIFIED_GROSS_WEIGHT,
                  containerInfo?.[ShipmentContainerInfoDetails.VERIFIED_GROSS_WEIGHT]
                )
              }
              disabled={viewOnly}
            />
          </p>
        </Grid>
      </Grid>
    </div>
  );
};
